import React, { useMemo } from 'react';
import { TableCell, TableRow } from '@mui/material';
import { RoomState } from '../../../../../../types/properties/propertyTypes';
import roomImagePlaceholder from '../../../../../../images/room-placeholder.svg';
import Tag from '../../../../../../muiComponents/Tag';
import variables from '../../../../../../stylesheets/variables.module.scss';
import Menu from '../../../../components/Menu';
import { useHistory } from 'react-router-dom';
import {
    ROOM_INITIAL_STATE,
    URL_HOTEL_BASE,
    URL_INVENTORY_LIST,
    URL_TAB_PARAM,
} from '../../../../../../utils/property/propertyUtils';
import encodeBase64 from '../../../../../../utils/encodeBase64';
import {
    useProperty,
    usePropertyFetching,
    usePropertyPresentation,
    usePropertyProgress,
    usePropertyRoomDetail,
} from '../../../../../contextsProperty/ContextProperties';
import convertToOvariant from '../../../../../../utils/convertToOvariant';
import {
    savableEntities,
    viewModelEntities,
} from '../../../../../../utils/commonVariables/entityKeys';
import endpoints from '../../../../../../utils/commonVariables/endpoints';
import api from '../../../../../../services/api';
import { useNotification } from '../../../../../../contexts/NotificationsContext';
import supplierHotelRoomTypeContract from '../../../../../../utils/viewModelStructures/SupplierHotelRoomType';
import LoadingPlaceholder from '../../../../../../muiComponents/LoadingPlaceholder';
import useUserProfile from '../../../../../../customHooks/useUserProfile';

interface RoomItemProps {
    room: RoomState;
}

const RoomItem = (props: RoomItemProps) => {
    const { room } = props;
    const roomImage = room.images?.[0]?.url;
    const idEncoded = encodeBase64(room.id);
    const { id } = useProperty();
    const history = useHistory();
    const { handleFetching, isFetching } = usePropertyFetching();
    const { deleteStateRoom } = usePropertyPresentation();
    const { totalProgress } = usePropertyProgress();
    const { addNewRoom, handleTargetRoom } = usePropertyRoomDetail();
    const { setErrorMessage, setSuccessMessage } = useNotification();
    const { readOnlyUser } = useUserProfile();

    async function handleDelete() {
        try {
            handleFetching(true);
            const payload = convertToOvariant(
                savableEntities.SupplierHotelRoomType,
                {
                    Ids: [room.id],
                }
            );
            const endpoint = endpoints.commonDeleteItems;
            await api.post(endpoint, payload);
            deleteStateRoom(room.id);
            setSuccessMessage('Your_data_has_been_deleted_successfully');
        } catch (error) {
            setErrorMessage('something_went_wrong');
        } finally {
            handleFetching(false);
        }
    }

    async function handleClone() {
        const endpoint = endpoints.viewModel(
            viewModelEntities.SupplierHotelRoomType,
            room.id
        );
        const { data } = await api.post(
            endpoint,
            supplierHotelRoomTypeContract
        );
        const newRoom = { ...data.Result };
        const newRoomId = '-1';
        newRoom.Id = newRoomId;
        newRoom.Name += ' copy';
        // Al clonar estos tres campos NO deben copiarse ver ticket #13748
        // no se piden en el view model pero se añaden aquí
        newRoom.MappingCode = '';
        newRoom.Enabled = false;
        newRoom.Active = false;
        if (newRoom.MinAdults === 0) {
            newRoom.Occupancy = '';
            newRoom.MinAdults = 1;
        }
        if (newRoom.MaxAdults === 0) {
            newRoom.Occupancy = '';
            newRoom.MaxAdults = 1;
        }
        if (newRoom.MaxChild < 0 || newRoom.MinChildren < 0) {
            newRoom.Occupancy = '';
            newRoom.MaxChild = 0;
            newRoom.MinChildren = 0;
        }
        const newBeds = newRoom.SupplierHotelRoomBedTypes?.map((bed: any) => ({
            ...bed,
            __isChanged: true,
            __isAdded: true,
        }));
        newRoom.SupplierHotelRoomBedTypes = newBeds;
        const newServices = newRoom.Services?.map((service: any) => ({
            ...service,
            __isChanged: true,
            __isAdded: true,
        }));
        newRoom.Services = newServices;

        const objectToSend = convertToOvariant(
            savableEntities.SupplierHotelRoomType,
            newRoom,
            newRoomId
        );
        try {
            handleFetching(true);
            const responseSave = await api.post(
                endpoints.commonSave,
                objectToSend
            );
            const newRoomState = {
                ...ROOM_INITIAL_STATE,
                id: responseSave.data.Id,
                name: responseSave.data.Name,
            };
            addNewRoom(newRoomState);
            handleTargetRoom(newRoomState.id);
            setSuccessMessage('Your_data_has_been_created_successfully');

            history.push(
                `${URL_HOTEL_BASE(id)}${URL_INVENTORY_LIST}/${encodeBase64(
                    responseSave.data.Id
                )}${URL_TAB_PARAM(1)}`
            );
        } catch (error) {
            console.log(error);
            setErrorMessage('something_went_wrong');
        } finally {
            handleFetching(false);
        }
    }

    const menuItems = useMemo(() => {
        const items = [
            {
                text: 'edit',
                icon: 'Edit',
                onClick: () => {
                    `${URL_HOTEL_BASE(
                        id
                    )}${URL_INVENTORY_LIST}/${idEncoded}${URL_TAB_PARAM(1)}`;
                },
            },
            {
                text: 'clone',
                icon: 'ContentCopy',
                onClick: () => {
                    handleClone();
                },
            },
            {
                text: 'delete',
                icon: 'Delete',
                onClick: () => {
                    handleDelete();
                },
            },
            {
                type: 'divider',
            },
            {
                text: 'occupancy',
                icon: 'MeetingRoom',
                onClick: () => {
                    history.push(
                        `${URL_HOTEL_BASE(
                            id
                        )}${URL_INVENTORY_LIST}/${idEncoded}${URL_TAB_PARAM(2)}`
                    );
                },
            },
            {
                text: 'services',
                icon: 'RoomService',
                onClick: () => {
                    history.push(
                        `${URL_HOTEL_BASE(
                            id
                        )}${URL_INVENTORY_LIST}/${idEncoded}${URL_TAB_PARAM(3)}`
                    );
                },
            },
            {
                text: 'bed_types',
                icon: 'Bed',
                onClick: () => {
                    history.push(
                        `${URL_HOTEL_BASE(
                            id
                        )}${URL_INVENTORY_LIST}/${idEncoded}${URL_TAB_PARAM(4)}`
                    );
                },
            },
            {
                text: 'Images',
                icon: 'Image',
                onClick: () => {
                    history.push(
                        `${URL_HOTEL_BASE(
                            id
                        )}${URL_INVENTORY_LIST}/${idEncoded}${URL_TAB_PARAM(5)}`
                    );
                },
            },
        ];

        return totalProgress >= 95
            ? items
            : items.filter((item, ind: number) => ind <= 2);
    }, [totalProgress]);

    return (
        <TableRow
            key={room.id}
            className='completed-presentation__room'
            onClick={() => {
                !isFetching &&
                    !readOnlyUser &&
                    history.push(
                        `${URL_HOTEL_BASE(
                            id
                        )}${URL_INVENTORY_LIST}/${idEncoded}${URL_TAB_PARAM(1)}`
                    );
            }}
        >
            <TableCell sx={{ width: 215 }}>
                {isFetching ? (
                    <LoadingPlaceholder
                        variant='rounded'
                        width={215}
                        height={100}
                    />
                ) : (
                    <img
                        src={roomImage || roomImagePlaceholder}
                        alt='room image'
                        className={
                            roomImage
                                ? 'completed-presentation__image-room'
                                : 'completed-presentation__image-room--placeholder'
                        }
                    />
                )}
            </TableCell>
            <TableCell className='width100perc'>
                {isFetching ? (
                    <LoadingPlaceholder variant='text' width={'100%'} />
                ) : (
                    <>
                        <h6 className='room__title'>{room.name}</h6>
                        <p className='room__description'>{room.description}</p>
                    </>
                )}
            </TableCell>

            <TableCell style={{ width: 200 }}>
                <div style={{ width: 200 }} className='flex flex-wrap gap-5'>
                    {isFetching ? (
                        <LoadingPlaceholder variant='text' width={'100%'} />
                    ) : (
                        room?.services?.map(service => (
                            <Tag
                                style={{ padding: '2px 4px' }}
                                key={service.id}
                                text={service.name}
                                variant='outlined'
                                color={variables.accentColorCyan400}
                            />
                        ))
                    )}
                </div>
            </TableCell>
            <TableCell style={{ width: 120 }}>
                <div style={{ width: 120 }} className='flex flex-wrap gap-5'>
                    {isFetching ? (
                        <LoadingPlaceholder variant='text' width={'100%'} />
                    ) : (
                        room?.bedTypes?.map(bed => (
                            <Tag
                                style={{ padding: '2px 4px' }}
                                key={bed.id}
                                text={bed.name}
                                variant='outlined'
                                color={variables.accentColorPurple400}
                            />
                        ))
                    )}
                </div>
            </TableCell>
            <TableCell style={{ width: 72 }}>
                {isFetching ? (
                    <LoadingPlaceholder variant='rounded' width={40} />
                ) : readOnlyUser ? (
                    ''
                ) : (
                    <Menu menuItems={menuItems} />
                )}
            </TableCell>
        </TableRow>
    );
};

export default RoomItem;
